/**
 * Logger utility for tracking user-related activities
 * Currently uses console.log, but can be extended to post logs to a service
 *
 * @example
 * // Basic usage with pCode and action
 * import { logger } from './utils/logger';
 * import { useAppContext } from './context/AppContext';
 *
 * const { pCode, user } = useAppContext();
 *
 * // Log a user action
 * logger.info(pCode, 'User created a new property', user.iamUserId, user.email);
 *
 * // Log with metadata
 * logger.info(
 *   pCode,
 *   'User updated theme settings',
 *   user.iamUserId,
 *   user.email,
 *   { themeId: '123', changes: ['color', 'font'] }
 * );
 *
 * // Log errors
 * logger.error(pCode, 'Failed to save property', user.iamUserId, user.email, { error: error.message });
 *
 * // Log warnings
 * logger.warn(pCode, 'User attempted invalid action', user.iamUserId, user.email);
 *
 * // Get current session ID for debugging
 * const currentSession = logger.getSessionId();
 * console.log('Current session:', currentSession);
 */

export interface LogMetadata {
    [key: string]: any;
  }
  
  export interface UserActivityLog {
    timestamp: string;
    sessionId: string;
    level: LogLevel;
    pCode: string;
    userId?: string;
    userEmail?: string;
    action: string;
    metadata?: LogMetadata;
  }
  
  /**
   * Log levels for different types of activities
   */
  export enum LogLevel {
    INFO = 'INFO',
    WARN = 'WARN',
    ERROR = 'ERROR',
    DEBUG = 'DEBUG',
  }
  
  class Logger {
    private isDevelopment: boolean;
    private sessionId: string;
  
    constructor() {
      this.isDevelopment = import.meta.env.DEV || import.meta.env.MODE === 'development';
      this.sessionId = this.getOrCreateSessionId();
    }
  
    /**
     * Gets existing session ID from sessionStorage or creates a new one
     * Session persists across page refreshes but is unique per browser tab
     */
    private getOrCreateSessionId(): string {
      const SESSION_KEY = 'cmp_portal_session_id';
      
      // Try to get existing session ID
      let sessionId = sessionStorage.getItem(SESSION_KEY);
      
      if (!sessionId) {
        // Generate new session ID using crypto API or fallback
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
          sessionId = crypto.randomUUID();
        } else {
          // Fallback for older browsers
          sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        }
        sessionStorage.setItem(SESSION_KEY, sessionId);
      }
      
      return sessionId;
    }
  
    /**
     * Gets the current session ID
     */
    getSessionId(): string {
      return this.sessionId;
    }
  
    /**
     * Creates a log entry with user activity information
     */
    private createLogEntry(
      pCode: string,
      action: string,
      level: LogLevel,
      userId?: string,
      userEmail?: string,
      metadata?: LogMetadata,
    ): UserActivityLog {
      return {
        timestamp: new Date().toISOString(),
        sessionId: this.sessionId,
        level,
        pCode,
        userId,
        userEmail,
        action,
        metadata,
      };
    }
  
    /**
     * Formats the log entry for console output
     */
    private formatLogEntry(logEntry: UserActivityLog, level: LogLevel): string {
      const prefix = `[${level}] [${logEntry.timestamp}]`;
      const sessionInfo = `Session: ${logEntry.sessionId}`;
      const pCodeInfo = `pCode: ${logEntry.pCode}`;
      const userInfo = logEntry.userId
        ? `userId: ${logEntry.userId}${logEntry.userEmail ? `, email: ${logEntry.userEmail}` : ''}`
        : '';
      const actionInfo = `Action: ${logEntry.action}`;
      const metadataInfo = logEntry.metadata
        ? `Metadata: ${JSON.stringify(logEntry.metadata, null, 2)}`
        : '';
  
      return [prefix, sessionInfo, pCodeInfo, userInfo, actionInfo, metadataInfo]
        .filter(Boolean)
        .join(' | ');
    }
  
    /**
     * Logs user activity to console
     * @param pCode - Property code
     * @param action - Description of the user action
     * @param level - Log level (default: INFO)
     * @param userId - Optional user ID
     * @param userEmail - Optional user email
     * @param metadata - Optional additional metadata
     */
    log(
      pCode: string,
      action: string,
      level: LogLevel = LogLevel.INFO,
      userId?: string,
      userEmail?: string,
      metadata?: LogMetadata,
    ): void {
      const logEntry = this.createLogEntry(pCode, action, level, userId, userEmail, metadata);
      const formattedLog = this.formatLogEntry(logEntry, level);
  
      // Use appropriate console method based on log level
      switch (level) {
        case LogLevel.ERROR:
          console.error(formattedLog, logEntry);
          break;
        case LogLevel.WARN:
          console.warn(formattedLog, logEntry);
          break;
        case LogLevel.DEBUG:
          if (this.isDevelopment) {
            console.debug(formattedLog, logEntry);
          }
          break;
        case LogLevel.INFO:
        default:
          console.log(formattedLog, logEntry);
          break;
      }
  
      if (import.meta.env.VITE_NODE_ENV === 'production') {
        this.postLogToService(logEntry);
      }
    }
  
    /**
     * Convenience method for logging info level activities
     */
    info(
      pCode: string,
      action: string,
      userId?: string,
      userEmail?: string,
      metadata?: LogMetadata,
    ): void {
      this.log(pCode, action, LogLevel.INFO, userId, userEmail, metadata);
    }
  
    /**
     * Convenience method for logging warning level activities
     */
    warn(
      pCode: string,
      action: string,
      userId?: string,
      userEmail?: string,
      metadata?: LogMetadata,
    ): void {
      this.log(pCode, action, LogLevel.WARN, userId, userEmail, metadata);
    }
  
    /**
     * Convenience method for logging error level activities
     */
    error(
      pCode: string,
      action: string,
      userId?: string,
      userEmail?: string,
      metadata?: LogMetadata,
    ): void {
      this.log(pCode, action, LogLevel.ERROR, userId, userEmail, metadata);
    }
  
    /**
     * Convenience method for logging debug level activities (only in development)
     */
    debug(
      pCode: string,
      action: string,
      userId?: string,
      userEmail?: string,
      metadata?: LogMetadata,
    ): void {
      this.log(pCode, action, LogLevel.DEBUG, userId, userEmail, metadata);
    }
  
    /**
     * Sends log entries to the backend service
     * 
     * The logEntry includes 'level' field which can be used for:
     * - Creating metrics based on log levels (e.g., error_count, warning_count)
     * - Setting up alerts (e.g., alert when error rate exceeds threshold)
     * - Filtering logs by severity in your logging system
     * - Building dashboards showing log distribution by level
     */
    private async postLogToService(logEntry: UserActivityLog): Promise<void> {
      try {
        await fetch('https://test-api.cmp.inmobi.com/lambda/ui-activity-logs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(logEntry),
          keepalive: true, // ensures logs send even on page unload
        });
      } catch (error) {
        console.error('Failed to send log to backend:', error);
      }
    }
  }
  
  // Export a singleton instance
  export const logger = new Logger();
  
  // Export the Logger class for testing purposes
  export default Logger;